import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Mail, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { API } from '../lib/api';

export default function ForgotPasswordPage() {
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(`${API}/guest/request-password-reset`, { email });
      setSubmitted(true);
    } catch (err) {
      // Even on error, show success message to prevent enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };
  
  const renderForm = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto bg-gradient-to-br from-[#1B4332] to-[#2D5A45] rounded-2xl flex items-center justify-center mb-4 shadow-lg">
          <Mail className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-2xl font-cinzel font-semibold text-gray-900 mb-2">
          {language === 'en' ? 'Forgot Your Password?' : 'Password Dimenticata?'}
        </h1>
        <p className="text-gray-600 text-sm">
          {language === 'en' 
            ? 'Enter your email and we\'ll send you a link to reset your password.'
            : 'Inserisci la tua email e ti invieremo un link per reimpostare la password.'}
        </p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium text-gray-700">
            Email
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#C5A059]" />
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={language === 'en' ? 'your@email.com' : 'tua@email.com'}
              className="pl-10 h-12"
              required
              data-testid="forgot-email-input"
            />
          </div>
        </div>
        
        {error && (
          <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        
        <Button
          type="submit"
          disabled={loading || !email}
          className="w-full h-12 bg-[#1B4332] hover:bg-[#2D5A45] text-white font-medium"
          data-testid="submit-forgot-btn"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            language === 'en' ? 'Send Reset Link' : 'Invia Link di Reset'
          )}
        </Button>
      </form>
      
      <div className="text-center">
        <Link 
          to="/login" 
          className="inline-flex items-center text-[#C5A059] hover:underline text-sm font-medium"
          data-testid="back-to-login-link"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          {language === 'en' ? 'Back to Login' : 'Torna al Login'}
        </Link>
      </div>
    </div>
  );
  
  const renderSuccess = () => (
    <div className="text-center space-y-6">
      <div className="w-16 h-16 mx-auto bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg">
        <CheckCircle className="w-8 h-8 text-white" />
      </div>
      
      <div>
        <h1 className="text-2xl font-cinzel font-semibold text-gray-900 mb-2">
          {language === 'en' ? 'Check Your Email' : 'Controlla la Tua Email'}
        </h1>
        <p className="text-gray-600 text-sm">
          {language === 'en' 
            ? 'If an account exists for this email, you will receive a password reset link.'
            : 'Se esiste un account associato a questa email, riceverai un link per reimpostare la password.'}
        </p>
      </div>
      
      <div className="bg-blue-50 rounded-xl p-4 text-sm text-blue-800">
        <p className="font-medium mb-1">
          {language === 'en' ? 'Didn\'t receive the email?' : 'Non hai ricevuto l\'email?'}
        </p>
        <ul className="text-blue-700 space-y-1">
          <li>• {language === 'en' ? 'Check your spam folder' : 'Controlla la cartella spam'}</li>
          <li>• {language === 'en' ? 'Wait a few minutes' : 'Attendi qualche minuto'}</li>
          <li>• {language === 'en' ? 'Make sure you entered the correct email' : 'Verifica di aver inserito l\'email corretta'}</li>
        </ul>
      </div>
      
      <div className="space-y-3">
        <Button
          variant="outline"
          onClick={() => {
            setSubmitted(false);
            setEmail('');
          }}
          className="w-full border-[#C5A059] text-[#C5A059] hover:bg-[#C5A059] hover:text-white"
          data-testid="try-again-btn"
        >
          {language === 'en' ? 'Try Another Email' : 'Prova con un\'altra Email'}
        </Button>
        
        <Link to="/login">
          <Button
            variant="ghost"
            className="w-full text-gray-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'en' ? 'Back to Login' : 'Torna al Login'}
          </Button>
        </Link>
      </div>
    </div>
  );
  
  return (
    <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardContent className="p-8">
          {submitted ? renderSuccess() : renderForm()}
        </CardContent>
      </Card>
    </div>
  );
}
